import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent } from '@mui/material';
import { FilterData } from 'types/types';
import FiltersContainerComponent from 'components/Filters/FiltersContainerComponent';
import { filters } from 'features/filtersState';
import PodData from './PodData';
import LocalForageService from 'services/LocalForageService';

interface PodState<T> {
  podId: string;
  filters: FilterData[];
  data: T;
  related_filters: string[];
  type: string;
  title: string;
}

interface PodProps {
  dashboardId: string;
  pod: PodState<any>;
}

const PodContainer: React.FC<PodProps> = ({ dashboardId, pod }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const filtersState = useSelector(filters);
  const podDataRef = useRef<HTMLDivElement>(null);
  const lsService = new LocalForageService();

  const goFullScreen = () => {
    if (podDataRef.current) {
      const elem = podDataRef.current as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
        msRequestFullscreen?: () => Promise<void>;
      };

      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE11 */
        elem.msRequestFullscreen();
      }
    }
  };

  useEffect(() => {
    let allFiltersLoaded = true;
    if (!pod.filters || pod.filters.length === 0) {
      allFiltersLoaded = true;
    } else {
      pod.filters?.forEach((filter) => {
        let loadedFilters = 0;
        filter.cascadeParentFilters?.forEach((parentFilterId) => {
          const parentFilter = filtersState.find(
            (f) =>
              f.filterId === parentFilterId && f.dashboardId === dashboardId
          );
          if (
            parentFilter &&
            parentFilter.selectedValues &&
            parentFilter.selectedValues.length > 0 &&
            parentFilter.loaded
          ) {
            loadedFilters++;
          }
        });
        if (loadedFilters !== (filter.cascadeParentFilters?.length || 0)) {
          allFiltersLoaded = false;
        }
      });
    }

    if (allFiltersLoaded) {
      setLoading(false);
    }
  }, [filtersState, pod.filters, dashboardId, loading]);
  const refreshPod = () => {
    lsService.removeItem(`pod_data_${dashboardId}_${pod.podId}`);
    // Add the refreshPod function
    setLoading(true);
  };

  return (
    <>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Box className={`dashboard-view-container type-${pod.type}`}>
          <Card className="dashboard-view-pods-container-card">
            <Box
              className="dashboard-view-pods-container-card-header"
              sx={{ display: 'flex' }}
            >
              <Box className="dashboard-pod-title-container">
                <span className="dashboard-pod-title">{pod.title}</span>
              </Box>
              <Box flexGrow={1}>
                <div>
                  <FiltersContainerComponent
                    dashboardId={dashboardId}
                    initialFilters={pod.filters}
                    podId={pod.podId}
                    goFullScreen={goFullScreen}
                    refreshPod={refreshPod}
                  />
                </div>
              </Box>
            </Box>
            <CardContent
              ref={podDataRef}
              className="dashboard-view-pods-container-card-content"
              sx={{
                height: 'calc(100% - 40px)',
              }}
            >
              <PodData pod={pod} dashboardId={dashboardId} />
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
};

export default PodContainer;
